import React, { useRef, useCallback } from 'react';
import { Group, Line, Circle } from 'react-konva';

// Offset in world-coordinate inches to raise the drag handle above the finger on touch
const TOUCH_OFFSET_PX = 50; // pixels on screen before converting to world units

function FloorPlanWall({ wall, isSelected, onSelect, onDragEnd, onWallDrag, zoom, activeTool }) {
  const handleSize = Math.max(4, 6 / zoom);
  const isTouchRef = useRef(false);
  const wallDragStartRef = useRef(null);

  // Touch offset in world units (depends on current zoom)
  const touchOffsetWorld = TOUCH_OFFSET_PX / zoom;

  const handleEndpointDragMove = useCallback((e) => {
    // On touch, shift the handle up so user can see it above their finger
    if (isTouchRef.current) {
      const node = e.target;
      node.y(node.y() - touchOffsetWorld);
    }
  }, [touchOffsetWorld]);

  const handleTouchStart = useCallback(() => {
    isTouchRef.current = true;
  }, []);

  const handleMouseDown = useCallback(() => {
    isTouchRef.current = false;
  }, []);

  // Wall body drag handlers
  const handleWallDragStart = useCallback((e) => {
    wallDragStartRef.current = { x: e.target.x(), y: e.target.y() };
  }, []);

  const handleWallDragMove = useCallback((e) => {
    if (isTouchRef.current) {
      const node = e.target;
      node.y(node.y() - touchOffsetWorld);
    }
  }, [touchOffsetWorld]);

  const handleWallDragEnd = useCallback((e) => {
    const dx = e.target.x() - (wallDragStartRef.current?.x || 0);
    const dy = e.target.y() - (wallDragStartRef.current?.y || 0);
    e.target.position(wallDragStartRef.current || { x: 0, y: 0 });
    wallDragStartRef.current = null;
    if (dx !== 0 || dy !== 0) {
      onWallDrag(wall.id, {
        x1: wall.x1 + dx,
        y1: wall.y1 + dy,
        x2: wall.x2 + dx,
        y2: wall.y2 + dy,
      });
    }
  }, [wall, onWallDrag]);

  const isSelectTool = activeTool === 'select';

  return (
    <Group>
      {/* Wall line */}
      <Line
        points={[wall.x1, wall.y1, wall.x2, wall.y2]}
        stroke={isSelected ? '#C4612E' : '#333333'}
        strokeWidth={wall.thickness / zoom}
        hitStrokeWidth={Math.max(12 / zoom, wall.thickness / zoom + 4)}
        lineCap="round"
        draggable={isSelected}
        onTouchStart={handleTouchStart}
        onMouseDown={handleMouseDown}
        onClick={(e) => {
          if (!isSelectTool) return;
          e.cancelBubble = true;
          onSelect(wall.id);
        }}
        onTap={(e) => {
          if (!isSelectTool) return;
          e.cancelBubble = true;
          onSelect(wall.id);
        }}
        onDragStart={handleWallDragStart}
        onDragMove={handleWallDragMove}
        onDragEnd={handleWallDragEnd}
        style={{ cursor: isSelected ? 'move' : 'pointer' }}
      />

      {/* Selection handles */}
      {isSelected && (
        <>
          <Circle
            x={wall.x1}
            y={wall.y1}
            radius={handleSize}
            fill="#C4612E"
            stroke="#fff"
            strokeWidth={1.5 / zoom}
            draggable
            onTouchStart={handleTouchStart}
            onMouseDown={handleMouseDown}
            onDragMove={handleEndpointDragMove}
            onDragEnd={(e) => {
              const finalX = e.target.x();
              const finalY = e.target.y();
              onDragEnd(wall.id, {
                x1: finalX,
                y1: finalY,
              });
              e.target.position({ x: 0, y: 0 });
              isTouchRef.current = false;
            }}
            style={{ cursor: 'move' }}
          />
          <Circle
            x={wall.x2}
            y={wall.y2}
            radius={handleSize}
            fill="#C4612E"
            stroke="#fff"
            strokeWidth={1.5 / zoom}
            draggable
            onTouchStart={handleTouchStart}
            onMouseDown={handleMouseDown}
            onDragMove={handleEndpointDragMove}
            onDragEnd={(e) => {
              const finalX = e.target.x();
              const finalY = e.target.y();
              onDragEnd(wall.id, {
                x2: finalX,
                y2: finalY,
              });
              e.target.position({ x: 0, y: 0 });
              isTouchRef.current = false;
            }}
            style={{ cursor: 'move' }}
          />
        </>
      )}
    </Group>
  );
}

export default React.memo(FloorPlanWall);
